import React from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import {
  FilterRangeSlider,
  FilterMultiSelect,
  FilterOneSelect,
  FilterColorSelect,
  FilterDateTimeRange,
  FilterTimeRange,
  FilterPercentRange,
  FilterSearch,
} from '../../../../Components';


const FilterTree = (
  ({
    /* required data ‑‑ all injected by SideBar */
    data,
    isChild = false,
    parent = [],
    tree = {},

    /* UI‑state & helpers coming from SideBar */
    expandedAccordions,
    toggleAccordion,
    selectedItems,
    setSelectedItems,
    handleAddNode,
    handleUpdateNode,
    handleRemoveNode,
    handleResetFilter,
    labels = {},
    pathExists,
  }) => {
    if (!data) return null;

    /** Returns true if a full nested path already exists in the tree  */
    const localPathExists = (fullPath) =>
      pathExists ? pathExists(fullPath) : false;

    return (
      <>
        {Object.keys(data).map((key) => {
          const item = data[key];
          const {
            specification_name,
            field_is_group,
            is_group,
            is_open,
            field_type,
            show_filter_as,
            field_name,
            values,
            filter_info,
            uom_abbr,
            range_filter_type,
            round_filter_info,
          } = item;

          const currentPath = (is_group === 1 || field_is_group === 1)
            ? [...parent, { key, value: 1 }]
            : [...parent];

          /* ---------- GROUP / CATEGORY ---------- */
          if (is_group === 1 || field_is_group === 1) {
            return (
              <div
                key={key}
                className={`border-top-accordion ${isChild ? 'products-sidebar-accordion-child' : ''}`}
              >
                <Accordion
                  TransitionProps={{ unmountOnExit: false }}
                  expanded={expandedAccordions[key] ?? is_open}
                  onChange={() => toggleAccordion(key, is_open)}
                  data-field-name={specification_name}
                  className={`products-sidebar-accordion ${
                    (expandedAccordions[key] ?? is_open) ? 'opened' : 'closed'
                  }`}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon className="products-sidebar-accordion-icon" />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                    className="products-sidebar-accordion-content"
                  >
                    {specification_name}
                  </AccordionSummary>

                  <AccordionDetails className="products-sidebar-accordion-details">
                    <FilterTree
                      data={field_is_group === 1 ? item.children : item}
                      isChild
                      parent={currentPath}
                      tree={tree}
                      /* re‑pump state & helpers downwards */
                      expandedAccordions={expandedAccordions}
                      toggleAccordion={toggleAccordion}
                      selectedItems={selectedItems}
                      setSelectedItems={setSelectedItems}
                      handleAddNode={handleAddNode}
                      handleUpdateNode={handleUpdateNode}
                      handleRemoveNode={handleRemoveNode}
                      handleResetFilter={handleResetFilter}
                      labels={labels}
                      pathExists={pathExists}
                    />
                  </AccordionDetails>
                </Accordion>
              </div>
            );
          }

          /* ---------- LEAF FIELD TYPES ---------- */
          /* 1) Numeric or float range sliders ------------------------------ */
          if (
            ['Number', 'Number Range', 'Float', 'Float Range'].includes(field_type) &&
            show_filter_as === 'Range'
          ) {
            const sliderValue = selectedItems[key];
            const showReset = sliderValue !== undefined;

            return (
              <div key={field_name} className={`border-top-accordion ${isChild ? 'products-sidebar-accordion-child' : ''}`}>
                <Accordion
                  TransitionProps={{ unmountOnExit: false }}
                  expanded={expandedAccordions[key] ?? is_open}
                  onChange={() => toggleAccordion(key, is_open)}
                  data-field-name={field_name}
                  className={`products-sidebar-accordion ${
                    (expandedAccordions[key] ?? is_open) ? 'opened' : 'closed'
                  }`}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon className="products-sidebar-accordion-icon" />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                    className="products-sidebar-accordion-content"
                  >
                    {field_name}
                    {showReset && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleResetFilter(key, currentPath);
                        }}
                        className="filter-reset-btn"
                      >
                        {/* tiny reset SVG */}
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M9.66167 1.66458C9.13738 1.13683 8.51384 0.718044 7.82697 0.432356C7.1401 0.146668 6.40349 -0.000272566 5.65958 3.79553e-07C2.52875 3.79553e-07 0 2.53583 0 5.66667C0 8.7975 2.52875 11.3333 5.65958 11.3333C8.30167 11.3333 10.5046 9.52708 11.135 7.08333H9.66167C9.36988 7.91174 8.8282 8.62918 8.11137 9.13668C7.39454 9.64417 6.53788 9.9167 5.65958 9.91667C3.315 9.91667 1.40958 8.01125 1.40958 5.66667C1.40958 3.32208 3.315 1.41667 5.65958 1.41667C6.83542 1.41667 7.88375 1.90542 8.64875 2.6775L6.36792 4.95833H11.3263V3.79553e-07L9.66167 1.66458Z" fill="#239DAB" />
                        </svg>
                      </button>
                    )}
                  </AccordionSummary>

                  <AccordionDetails className="products-sidebar-accordion-details">
                    <FilterRangeSlider
                      title={key}
                      item={item}
                      sliderValue={sliderValue}
                      min={filter_info?.min}
                      max={filter_info?.max}
                      uom={uom_abbr || ''}
                      filterType={range_filter_type}
                      round_filter_info={round_filter_info}
                      /* state handlers */
                      setSelectedItems={setSelectedItems}
                      addNode={handleAddNode}
                      updateNode={handleUpdateNode}
                      path={currentPath}
                    />
                  </AccordionDetails>
                </Accordion>
              </div>
            );
          }

          /* 2) Multiselects (simple string list) --------------------------- */
          if (
            ['Link', 'Link Multiselect'].includes(field_type) &&
            show_filter_as === 'Multiselect' &&
            typeof values?.[0] === 'string'
          ) {
            const isActive = selectedItems[key]?.length > 0;
            return (
              <div key={key} className={`border-top-accordion ${isChild ? 'products-sidebar-accordion-child' : ''}`}>
                <Accordion
                  TransitionProps={{ unmountOnExit: false }}
                  expanded={expandedAccordions[key] ?? is_open}
                  onChange={() => toggleAccordion(key, is_open)}
                  data-field-name={field_name}
                  className={`products-sidebar-accordion ${
                    (expandedAccordions[key] ?? is_open) ? 'opened' : 'closed'
                  }`}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon className="products-sidebar-accordion-icon" />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                    className="products-sidebar-accordion-content"
                  >
                    {field_name}
                    {isActive && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleResetFilter(key, currentPath);
                        }}
                        className="filter-reset-btn"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M9.66167 1.66458C9.13738 1.13683 8.51384 0.718044 7.82697 0.432356C7.1401 0.146668 6.40349 -0.000272566 5.65958 3.79553e-07C2.52875 3.79553e-07 0 2.53583 0 5.66667C0 8.7975 2.52875 11.3333 5.65958 11.3333C8.30167 11.3333 10.5046 9.52708 11.135 7.08333H9.66167C9.36988 7.91174 8.8282 8.62918 8.11137 9.13668C7.39454 9.64417 6.53788 9.9167 5.65958 9.91667C3.315 9.91667 1.40958 8.01125 1.40958 5.66667C1.40958 3.32208 3.315 1.41667 5.65958 1.41667C6.83542 1.41667 7.88375 1.90542 8.64875 2.6775L6.36792 4.95833H11.3263V3.79553e-07L9.66167 1.66458Z" fill="#239DAB" />
                        </svg>
                      </button>
                    )}
                  </AccordionSummary>

                  <AccordionDetails className="products-sidebar-accordion-details">
                    <FilterMultiSelect
                      title={key}
                      options={values || []}
                      selectedOptions={selectedItems[key] || []}
                      labels={labels}
                      /* state handlers */
                      setSelectedItems={setSelectedItems}
                      selectedItems={selectedItems}
                      addNode={handleAddNode}
                      removeNode={handleRemoveNode}
                      updateNode={handleUpdateNode}
                      path={currentPath}
                    />
                  </AccordionDetails>
                </Accordion>
              </div>
            );
          }

          if (
                (field_type === 'Link' || field_type === 'Link Multiselect') &&
                show_filter_as === 'Multiselect' &&
                values.length > 0 &&
                typeof values[0] === 'object'
            ) {
                return (
                <div key={key} className={`border-top-accordion ${isChild ? 'products-sidebar-accordion-child' : ''}`}>
                    <Accordion
                    TransitionProps={{ unmountOnExit: false }} 
                    expanded={expandedAccordions[key] ?? item.is_open}
                    onChange={() => toggleAccordion(key, item.is_open)}
                    data-field-name={field_name}
                    className={`products-sidebar-accordion ${
                        expandedAccordions[key] !== null && expandedAccordions[key] !== undefined
                        ? expandedAccordions[key]
                            ? 'opened'
                            : 'closed'
                        : item.is_open
                            ? 'opened'
                            : 'closed'
                    }`}
                    >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon className='products-sidebar-accordion-icon' />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                        className="products-sidebar-accordion-content"
                    >
                        {field_name}
                        {/* <button 
                            onClick={(e) => {
                            e.stopPropagation();
                            handleResetFilter(key, currentPath); // Pass currentPath here
                            }}
                            className="filter-reset-btn"
                        >
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M9.66167 1.66458C9.13738 1.13683 8.51384 0.718044 7.82697 0.432356C7.1401 0.146668 6.40349 -0.000272566 5.65958 3.79553e-07C2.52875 3.79553e-07 0 2.53583 0 5.66667C0 8.7975 2.52875 11.3333 5.65958 11.3333C8.30167 11.3333 10.5046 9.52708 11.135 7.08333H9.66167C9.36988 7.91174 8.8282 8.62918 8.11137 9.13668C7.39454 9.64417 6.53788 9.9167 5.65958 9.91667C3.315 9.91667 1.40958 8.01125 1.40958 5.66667C1.40958 3.32208 3.315 1.41667 5.65958 1.41667C6.83542 1.41667 7.88375 1.90542 8.64875 2.6775L6.36792 4.95833H11.3263V3.79553e-07L9.66167 1.66458Z" fill="#113B70"/>
                        </svg>
                        </button> */}
                    </AccordionSummary>
                    <AccordionDetails className="products-sidebar-accordion-details">
                        {values.map((groupObj) => {
                        const groupKey = Object.keys(groupObj)[0];
                        const groupData = groupObj[groupKey];
                        const groupValues = groupData.values || [];
                        const nestedKey = `${key}-${groupKey}`;
                        const hasFilter = (selectedItems[groupKey] || []).length > 0;

                
                        return (
                            <div key={nestedKey} className="border-top-accordion is-child">
                            <Accordion
                                TransitionProps={{ unmountOnExit: false }} 
                                expanded={expandedAccordions[nestedKey] || false}
                                onChange={() => toggleAccordion(nestedKey)}
                                className={`products-sidebar-accordion is-child ${
                                expandedAccordions[nestedKey] ? 'opened' : 'closed'
                                }`}
                            >


                                <AccordionSummary
                                expandIcon={<ExpandMoreIcon className='products-sidebar-accordion-icon' />}
                                aria-controls={`${nestedKey}-content`}
                                id={`${nestedKey}-header`}
                                className="products-sidebar-accordion-content"
                                >
                                {labels[groupKey]}
                                {hasFilter && (
                                    <button 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleResetFilter(groupKey, [...parent, { key }]); // Reset for this specific group
                                    }}
                                    className="filter-reset-btn"
                                    >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                        <path d="M9.66167 1.66458C9.13738 1.13683 8.51384 0.718044 7.82697 0.432356C7.1401 0.146668 6.40349 -0.000272566 5.65958 3.79553e-07C2.52875 3.79553e-07 0 2.53583 0 5.66667C0 8.7975 2.52875 11.3333 5.65958 11.3333C8.30167 11.3333 10.5046 9.52708 11.135 7.08333H9.66167C9.36988 7.91174 8.8282 8.62918 8.11137 9.13668C7.39454 9.64417 6.53788 9.9167 5.65958 9.91667C3.315 9.91667 1.40958 8.01125 1.40958 5.66667C1.40958 3.32208 3.315 1.41667 5.65958 1.41667C6.83542 1.41667 7.88375 1.90542 8.64875 2.6775L6.36792 4.95833H11.3263V3.79553e-07L9.66167 1.66458Z" fill="#113B70"/>
                                    </svg>
                                    </button>
                                )}
                                </AccordionSummary>
                                <AccordionDetails className="products-sidebar-accordion-details is-nested">
                                <FilterMultiSelect
                                title={groupKey}
                                options={groupValues}
                                selectedOptions={selectedItems[groupKey] || []}
                                setSelectedItems={setSelectedItems}
                                selectedItems={selectedItems}
                                addNode={handleAddNode}
                                removeNode={handleRemoveNode}
                                updateNode={handleUpdateNode}
                                path={[...parent, { key: key, value: 1 }]} 
                                labels={labels}
                                />
                                </AccordionDetails>
                            </Accordion>
                            </div>
                        );
                        })}
                    </AccordionDetails>
                    </Accordion>
                </div>
                );
            } if (field_type === 'Boolean' && show_filter_as === 'Checkbox' && item.is_group === 0) {
                return (
                    <div key={field_name} className="single-checkbox border-top-accordion">
                        <FilterOneSelect
                            title={key}
                            field_name={field_name}
                            setSelectedItems={setSelectedItems}
                            selectedItems={selectedItems}
                            addNode={handleAddNode}
                            removeNode={handleRemoveNode}
                            path={currentPath}
                        />
                    </div>
                );
            } if (field_type === 'Color' && show_filter_as === 'Multiselect') {

                const hasChanges = selectedItems[key] && selectedItems[key].length > 0;

                  return (
                      <div key={key} className={`border-top-accordion ${isChild ? 'products-sidebar-accordion-child' : ''}`}>
                          <Accordion
                              TransitionProps={{ unmountOnExit: false }} 
                              expanded={expandedAccordions[key] ?? item.is_open}
                              onChange={() => toggleAccordion(key, item.is_open)}
                              data-field-name={field_name} // Set the data attribute here
                              className={`products-sidebar-accordion ${expandedAccordions[key] !== null && expandedAccordions[key] !== undefined
                                  ? expandedAccordions[key]
                                      ? 'opened'
                                      : 'closed'
                                  : item.is_open
                                      ? 'opened'
                                      : 'closed'
                                  }`}
                          >
                              <AccordionSummary
                                  expandIcon={<ExpandMoreIcon className='products-sidebar-accordion-icon' />}
                                  aria-controls="panel1-content"
                                  id="panel1-header"
                                  className="products-sidebar-accordion-content"
                              >
                                  {field_name}
                                  {hasChanges && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleResetFilter(key, currentPath);
                                      }}
                                      className="filter-reset-btn"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                        <path d="M9.66167 1.66458C9.13738 1.13683 8.51384 0.718044 7.82697 0.432356C7.1401 0.146668 6.40349 -0.000272566 5.65958 3.79553e-07C2.52875 3.79553e-07 0 2.53583 0 5.66667C0 8.7975 2.52875 11.3333 5.65958 11.3333C8.30167 11.3333 10.5046 9.52708 11.135 7.08333H9.66167C9.36988 7.91174 8.8282 8.62918 8.11137 9.13668C7.39454 9.64417 6.53788 9.9167 5.65958 9.91667C3.315 9.91667 1.40958 8.01125 1.40958 5.66667C1.40958 3.32208 3.315 1.41667 5.65958 1.41667C6.83542 1.41667 7.88375 1.90542 8.64875 2.6775L6.36792 4.95833H11.3263V3.79553e-07L9.66167 1.66458Z" fill="#239DAB" />
                                      </svg>
                                    </button>
                                  )}
                              </AccordionSummary>
                              <AccordionDetails className="products-sidebar-accordion-details">
                                  <FilterColorSelect
                                      title={key}
                                      colors={values || []}
                                      setSelectedItems={setSelectedItems}
                                      selectedItems={selectedItems}
                                      addNode={handleAddNode}
                                      removeNode={handleRemoveNode}
                                      updateNode={handleUpdateNode}
                                      path={currentPath}
                                  />
                              </AccordionDetails>
                          </Accordion>
                      </div>
                  );
              } if ((field_type === 'Datetime' && show_filter_as === 'Range') ||
              (field_type === 'DateTime Range' && show_filter_as === 'Range')) {

     
              const rangeValue = selectedItems[key] || [null, null];
              return (
                  <div key={key} className={`border-top-accordion ${isChild ? 'products-sidebar-accordion-child' : ''}`}>
                      <Accordion
                          TransitionProps={{ unmountOnExit: false }} 
                          expanded={expandedAccordions[key] ?? item.is_open}
                          onChange={() => toggleAccordion(key, item.is_open)}
                          data-field-name={field_name} // Set the data attribute here
                          className={`products-sidebar-accordion ${expandedAccordions[key] !== null && expandedAccordions[key] !== undefined
                              ? expandedAccordions[key]
                                  ? 'opened'
                                  : 'closed'
                              : item.is_open
                                  ? 'opened'
                                  : 'closed'
                              }`}
                      >
                          <AccordionSummary
                              expandIcon={<ExpandMoreIcon className='products-sidebar-accordion-icon' />}
                              aria-controls="panel1-content"
                              id="panel1-header"
                              className="products-sidebar-accordion-content"
                          >
                              {field_name}
                              {/* <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleResetFilter(key, currentPath); // Pass currentPath here
                                  }}
                                  className="filter-reset-btn"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                  <path d="M9.66167 1.66458C9.13738 1.13683 8.51384 0.718044 7.82697 0.432356C7.1401 0.146668 6.40349 -0.000272566 5.65958 3.79553e-07C2.52875 3.79553e-07 0 2.53583 0 5.66667C0 8.7975 2.52875 11.3333 5.65958 11.3333C8.30167 11.3333 10.5046 9.52708 11.135 7.08333H9.66167C9.36988 7.91174 8.8282 8.62918 8.11137 9.13668C7.39454 9.64417 6.53788 9.9167 5.65958 9.91667C3.315 9.91667 1.40958 8.01125 1.40958 5.66667C1.40958 3.32208 3.315 1.41667 5.65958 1.41667C6.83542 1.41667 7.88375 1.90542 8.64875 2.6775L6.36792 4.95833H11.3263V3.79553e-07L9.66167 1.66458Z" fill="#113B70"/>
                                </svg>
                              </button> */}
                          </AccordionSummary>
                          <AccordionDetails className="products-sidebar-accordion-details">
                              <FilterDateTimeRange
                                  field_name={key}
                                  setSelectedItems={setSelectedItems}
                                  selectedItems={selectedItems}
                                  addNode={handleAddNode}
                                  removeNode={handleRemoveNode}
                                  updateNode={handleUpdateNode}
                                  path={currentPath}
                                  pathExists={pathExists}
                                  rangeValue={rangeValue}
                              />
                          </AccordionDetails>
                      </Accordion>
                  </div>
              );
            } if ((field_type === 'Time Range' && show_filter_as === 'Range') ||
            (field_type === 'Time' && show_filter_as === 'Range')) {
            const rangeValue = selectedItems[key] || [null, null];
            return (
                <div key={key} className={`border-top-accordion ${isChild ? 'products-sidebar-accordion-child' : ''}`}>
                    <Accordion
                        TransitionProps={{ unmountOnExit: false }} 
                        expanded={expandedAccordions[key] ?? item.is_open}
                        onChange={() => toggleAccordion(key, item.is_open)}
                        data-field-name={field_name} // Set the data attribute here
                        className={`products-sidebar-accordion ${expandedAccordions[key] !== null && expandedAccordions[key] !== undefined
                            ? expandedAccordions[key]
                                ? 'opened'
                                : 'closed'
                            : item.is_open
                                ? 'opened'
                                : 'closed'
                            }`}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon className='products-sidebar-accordion-icon' />}
                            aria-controls="panel1-content"
                            id="panel1-header"
                            className="products-sidebar-accordion-content"
                        >
                            {field_name}
                            {/* <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleResetFilter(key, currentPath); // Pass currentPath here
                                }}
                                className="filter-reset-btn"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path d="M9.66167 1.66458C9.13738 1.13683 8.51384 0.718044 7.82697 0.432356C7.1401 0.146668 6.40349 -0.000272566 5.65958 3.79553e-07C2.52875 3.79553e-07 0 2.53583 0 5.66667C0 8.7975 2.52875 11.3333 5.65958 11.3333C8.30167 11.3333 10.5046 9.52708 11.135 7.08333H9.66167C9.36988 7.91174 8.8282 8.62918 8.11137 9.13668C7.39454 9.64417 6.53788 9.9167 5.65958 9.91667C3.315 9.91667 1.40958 8.01125 1.40958 5.66667C1.40958 3.32208 3.315 1.41667 5.65958 1.41667C6.83542 1.41667 7.88375 1.90542 8.64875 2.6775L6.36792 4.95833H11.3263V3.79553e-07L9.66167 1.66458Z" fill="#113B70"/>
                              </svg>
                            </button> */}
                        </AccordionSummary>
                        <AccordionDetails className="products-sidebar-accordion-details">
                            <FilterTimeRange
                                field_name={key}
                                setSelectedItems={setSelectedItems}
                                selectedItems={selectedItems}
                                addNode={handleAddNode}
                                removeNode={handleRemoveNode}
                                updateNode={handleUpdateNode}
                                path={currentPath}
                                pathExists={pathExists}
                                rangeValue={rangeValue}
                            />
                        </AccordionDetails>
                    </Accordion>
                </div>
            );
        } if ((field_type === 'Percent Range' && show_filter_as === 'Range')) {

            const sliderValue = selectedItems[key];

            const minValue = filter_info?.min ?? 0;
            const maxValue = filter_info?.max ?? 100;
            
            const showResetPercentButton = sliderValue !== undefined;

            return (
                <div key={key} className={`border-top-accordion ${isChild ? 'products-sidebar-accordion-child' : ''}`}>
                    <Accordion
                        TransitionProps={{ unmountOnExit: false }} 
                        expanded={expandedAccordions[key] ?? item.is_open}
                        onChange={() => toggleAccordion(key, item.is_open)}
                        data-field-name={field_name} // Set the data attribute here
                        className={`products-sidebar-accordion ${expandedAccordions[key] !== null && expandedAccordions[key] !== undefined
                            ? expandedAccordions[key]
                                ? 'opened'
                                : 'closed'
                            : item.is_open
                                ? 'opened'
                                : 'closed'
                            }`}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon className='products-sidebar-accordion-icon' />}
                            aria-controls="panel1-content"
                            id="panel1-header"
                            className="products-sidebar-accordion-content"
                        >
                            {field_name}
                            {showResetPercentButton && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleResetFilter(key, currentPath);
                                    }}
                                    className="filter-reset-btn"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                        <path d="M9.66167 1.66458C9.13738 1.13683 8.51384 0.718044 7.82697 0.432356C7.1401 0.146668 6.40349 -0.000272566 5.65958 3.79553e-07C2.52875 3.79553e-07 0 2.53583 0 5.66667C0 8.7975 2.52875 11.3333 5.65958 11.3333C8.30167 11.3333 10.5046 9.52708 11.135 7.08333H9.66167C9.36988 7.91174 8.8282 8.62918 8.11137 9.13668C7.39454 9.64417 6.53788 9.9167 5.65958 9.91667C3.315 9.91667 1.40958 8.01125 1.40958 5.66667C1.40958 3.32208 3.315 1.41667 5.65958 1.41667C6.83542 1.41667 7.88375 1.90542 8.64875 2.6775L6.36792 4.95833H11.3263V3.79553e-07L9.66167 1.66458Z" fill="#239DAB"/>
                                    </svg>
                                </button>
                            )}
                        </AccordionSummary>
                        <AccordionDetails className="products-sidebar-accordion-details">
                            <FilterPercentRange
                                field_name={key}
                                setSelectedItems={setSelectedItems}
                                addNode={handleAddNode}
                                updateNode={handleUpdateNode}
                                path={currentPath}
                                sliderValue={sliderValue}
                                min={minValue}
                                max={maxValue}
                            />
                        </AccordionDetails>
                    </Accordion>
                </div>
            );
        } if ((field_type === 'Text' && show_filter_as === 'Search')) {

            return (
                <div
                    key={field_name}
                    className={`border-top-accordion ${isChild ? 'products-sidebar-accordion-child' : ''}`}
                >
                    <Accordion
                        TransitionProps={{ unmountOnExit: false }} 
                        expanded={expandedAccordions[key] ?? item.is_open}
                        onChange={() => toggleAccordion(key, item.is_open)}
                        data-field-name={field_name} // Set the data attribute here
                        className={`products-sidebar-accordion ${expandedAccordions[key] !== null && expandedAccordions[key] !== undefined
                            ? expandedAccordions[key]
                                ? 'opened'
                                : 'closed'
                            : item.is_open
                                ? 'opened'
                                : 'closed'
                            }`}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon className="products-sidebar-accordion-icon" />}
                            aria-controls="panel1-content"
                            id="panel1-header"
                            className="products-sidebar-accordion-content"
                        >
                            {field_name}
                            {/* <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleResetFilter(key, currentPath); // Pass currentPath here
                                }}
                                className="filter-reset-btn"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path d="M9.66167 1.66458C9.13738 1.13683 8.51384 0.718044 7.82697 0.432356C7.1401 0.146668 6.40349 -0.000272566 5.65958 3.79553e-07C2.52875 3.79553e-07 0 2.53583 0 5.66667C0 8.7975 2.52875 11.3333 5.65958 11.3333C8.30167 11.3333 10.5046 9.52708 11.135 7.08333H9.66167C9.36988 7.91174 8.8282 8.62918 8.11137 9.13668C7.39454 9.64417 6.53788 9.9167 5.65958 9.91667C3.315 9.91667 1.40958 8.01125 1.40958 5.66667C1.40958 3.32208 3.315 1.41667 5.65958 1.41667C6.83542 1.41667 7.88375 1.90542 8.64875 2.6775L6.36792 4.95833H11.3263V3.79553e-07L9.66167 1.66458Z" fill="#113B70"/>
                              </svg>
                            </button> */}
                        </AccordionSummary>
                        <AccordionDetails className="products-sidebar-accordion-details">
                            <FilterSearch
                                field_name={key}
                                setSelectedItems={setSelectedItems}
                                selectedItems={selectedItems}
                                addNode={handleAddNode}
                                removeNode={handleRemoveNode}
                                updateNode={handleUpdateNode}
                                path={currentPath}
                                pathExists={pathExists}
                            />
                        </AccordionDetails>
                    </Accordion>

                    
                </div>

            );
        } 
          return null;
        })}
      </>
    );
  }
);

export default FilterTree;
